import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputComponent from 'webapp/components/form/input';
import FormSelectComponent from 'webapp/components/form/select';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type CustomMetricSetupFormApiMetricBuilder from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder';
import type HeaderOrQueryParameter from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder/header-or-query-parameter';

interface Args {
	form: CustomMetricSetupFormApiMetricBuilder;
	model: HeaderOrQueryParameter;
	isDisabled: boolean;
	theme: string;
	remove: () => void;
}

export default class CustomQuerySetupApiMetricBuilderParamsOrHeadersRowComponent extends Component<Args> {
	@service('constants') declare constantsService: Service.Constants;

	@tracked isDateTime = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		if (this.args.form.hasDateTimeFormat) {
			this.isDateTime = !!this.dateTimeOptions.find(
				(item) => item.value === this.args.model.value,
			);
		}
	}

	get isFieldDisabled(): boolean {
		return this.args.isDisabled || this.args.model.disabled;
	}

	get dateTimeOptions() {
		return this.constantsService.apiMetricBuilderTimeTokens;
	}

	get dateTimeFormatOptions(): LabelValuePairString[] {
		return this.args.form.dateTimeFormatOptions.map((item) => {
			return {
				value: item.id,
				label: item.name,
			};
		});
	}

	get selectedDescription(): string {
		const option = this.args.form.dateTimeFormatOptions.find(
			(item) => item.id === this.args.form.dateTimeFormat,
		);
		return option?.description || '';
	}

	@action
	changeValue(value: string): void {
		this.args.model.value = value;
	}

	@action
	toggleIsDateTime(): void {
		if (this.args.isDisabled) {
			return;
		}
		this.isDateTime = !this.isDateTime;
		if (this.isDateTime) {
			this.changeDateTimeFormat(this.dateTimeFormatOptions[0]?.value || '');
			this.changeValue(this.dateTimeOptions[0]?.value || '');
		} else {
			this.changeValue('');
		}
	}

	@action
	changeDateTimeFormat(value: string): void {
		this.args.form.dateTimeFormat = value;
	}
	<template>
		<div class="cloneable lineDown">
			<div class="size-1-5 item">
				<FormInputComponent
					name="key"
					@value={{@model.key}}
					placeholder="Key"
					disabled={{this.isFieldDisabled}}
					@theme={{@theme}}
				/>
				{{#unless @model.disabled}}
					{{#if @form.hasDateTimeFormat}}
						<a
							class="date-format-toggle {{if this.isDateTime 'selected'}}"
							{{on "click" this.toggleIsDateTime}}
						><CommonUiIconComponent @name="Calendar" @size="sm" />
						</a>
					{{/if}}
				{{/unless}}
			</div>
			<div class="size-1-5 item connected">
				{{#if this.isDateTime}}
					<FormSelectComponent
						name="datetime"
						@selected={{@model.value}}
						@options={{this.dateTimeOptions}}
						@onChange={{this.changeValue}}
						@isRequired={{true}}
						@isNumeric={{false}}
						@placeholder="Datetime"
						@isDisabled={{@isDisabled}}
						@theme={{@theme}}
					/>
				{{else}}
					<FormInputComponent
						name="value"
						@value={{@model.value}}
						placeholder="Value"
						disabled={{this.isFieldDisabled}}
						@theme={{@theme}}
					/>
				{{/if}}
			</div>

			{{#if this.isDateTime}}
				<div class="size-1-5 item connected">
					<FormSelectComponent
						name="datetimeformat"
						@selected={{@form.dateTimeFormat}}
						@options={{this.dateTimeFormatOptions}}
						@onChange={{this.changeDateTimeFormat}}
						@isRequired={{true}}
						@isNumeric={{false}}
						@placeholder="Datetime format"
						@isDisabled={{@isDisabled}}
						@theme={{@theme}}
					/>
					{{#if this.selectedDescription}}
						<ToolTipWithIconComponent
							@text={{this.selectedDescription}}
							@spaceRight={{true}}
						/>
					{{/if}}
				</div>
			{{/if}}

			{{#unless @model.disabled}}
				<a role="button" class="rem" {{on "click" @remove}}>
					<CommonUiIconComponent @name="Close" @size="sm" />
				</a>
			{{/unless}}

		</div>
	</template>
}
